import {useContext, useState} from "react";
import {Pagination, Select, Table} from "antd";
import {Button, Image, StatusLabel, PopperUserInfo} from "~/components";
import {renderDate, numberFormat, arrayToMap} from "~/utils";
import {useGroup} from "~/hooks";
import MarketingProjectAction from "./MarketingProjectAction";
import {AppContext} from "~/context/AppProvider";

const STATUS_OPTIONS = [
	{value: 'doing', label: 'Đang thực hiện'},
	{value: 'done', label: 'Đã thực hiện'},
	{value: 'paused', label: 'Tạm dừng'},
	{value: 'cancelled', label: 'Hủy'},
];

const AD_STATUS_OPTIONS = [
	{value: 'excellent', label: 'Rất tốt'},
	{value: 'good', label: 'Tốt'},
	{value: 'average', label: 'Trung bình'},
	{value: 'poor', label: 'Kém'},
];

//Header các cột ngân sách: nền đỏ đậm, chữ trắng + đệm 2 bên
const budgetHeader = () => ({style: {background: '#a40000', color: '#fff', paddingLeft: 16, paddingRight: 16}});
const budgetCell = () => ({style: {paddingLeft: 16, paddingRight: 16}});

const money = (v) => numberFormat(v || 0) + 'đ';

function MarketingProjectTable({items = [], pagination, onPaginationChange, setItemEdit, openModal, onChangeStatus, onChangeAdStatus, can}) {

	const {tableHeight} = useContext(AppContext);

	const listGroup = useGroup();

	const groupMap = arrayToMap(listGroup);

	const [editingCell, setEditingCell] = useState(null); // {id, field}

	const handleEditClick = (item) => {
		setItemEdit(item);
		openModal('addEdit');
	}

	const handleBudgetClick = (item) => {
		setItemEdit(item);
		openModal('budget');
	}

	const handleAssignClick = (item) => {
		setItemEdit(item);
		openModal('assign');
	}

	const handleReactivateClick = (item) => {
		setItemEdit(item);
		openModal('reactivate');
	}

	const handleDeleteClick = (item) => {
		setItemEdit(item);
		openModal('delete');
	}

	const isEditing = (item, field) => editingCell?.id === item.id && editingCell?.field === field;

	const columns = [
		{ title: 'Dự án', dataIndex: 'name', key: 'name', width: '240px', fixed: 'left', render: (_, item) => (
			<div>
				<b style={{color: 'var(--primary)'}}>{item.name}</b>
				<p>{item.service?.label && <StatusLabel small type={item.service.badge}>{item.service.label}</StatusLabel>}</p>
				{item.customer && <p style={{fontSize: '12px', color: '#888'}}>{item.customer.companyName || item.customer.name}</p>}
			</div>
		)},
		{ title: 'Link', dataIndex: 'reportLink', key: 'reportLink', width: '90px', render: (link) => (
			link ? <a href={link} target="_blank" rel="noreferrer">Báo cáo</a> : <span style={{color: '#ccc'}}>--</span>
		)},
		{ title: 'TG bắt đầu', dataIndex: 'startDate', key: 'startDate', width: '110px', render: (t) => renderDate(t)},
		{ title: 'TG kết thúc', dataIndex: 'endDate', key: 'endDate', width: '110px', render: (t) => renderDate(t)},
		{ title: 'Kinh doanh', dataIndex: 'user', key: 'user', width: '220px', render: (_, item) => (
			item.user ? (
				<PopperUserInfo user={item.user}>
					<div className="d-flex gap align-items">
						<div className="avatar"><Image src={item.user?.avatar} /></div>
						<div>
							<b style={{fontSize: '12px'}}>{item.user?.lastname}</b>
							<p style={{fontSize: '11px'}}>{(item?.groupId !== 0) ? groupMap[item?.groupId]?.name : item.user?.username}</p>
						</div>
					</div>
				</PopperUserInfo>
			) : <span style={{color: '#ccc'}}>--</span>
		)},
		{ title: 'Kỹ thuật', dataIndex: 'assign', key: 'assign', width: '200px', render: (_, item) => (
			item.assign ? (
				<PopperUserInfo user={item.assign}>
					<div className="d-flex gap align-items">
						<div className="avatar"><Image src={item.assign?.avatar} /></div>
						<div>
							<b style={{fontSize: '12px'}}>{item.assign?.lastname}</b>
							<p style={{fontSize: '11px'}}>{item.assign?.username}</p>
						</div>
					</div>
				</PopperUserInfo>
			) : <span style={{color: '#ccc'}}>Chưa phân</span>
		)},
		{ title: 'Thực thu', dataIndex: 'budgetActual', key: 'budgetActual', width: '130px', align: 'right', onHeaderCell: budgetHeader, onCell: budgetCell, render: (v) => <b>{money(v)}</b>},
		{ title: 'Sau thuế', dataIndex: 'budgetAfterTax', key: 'budgetAfterTax', width: '130px', align: 'right', onHeaderCell: budgetHeader, onCell: budgetCell, render: (v) => <b style={{color: '#a40000'}}>{money(v)}</b>},
		{ title: 'Ngân sách ngày', dataIndex: 'budgetDailyNeeded', key: 'budgetDailyNeeded', width: '140px', align: 'right', onHeaderCell: budgetHeader, onCell: budgetCell, render: money},
		{ title: 'Chi tiêu', dataIndex: 'budgetSpent', key: 'budgetSpent', width: '140px', align: 'right', onHeaderCell: budgetHeader, onCell: budgetCell, render: (v, item) => (
			<Button small background blue onClick={() => handleBudgetClick(item)} title="Xem chi tiết chi tiêu theo ngày">{money(v)}</Button>
		)},
		{ title: 'Còn lại', dataIndex: 'budgetRemaining', key: 'budgetRemaining', width: '130px', align: 'right', onHeaderCell: budgetHeader, onCell: budgetCell, render: (v) => <b style={{color: v < 0 ? '#a40000' : 'var(--green)'}}>{money(v)}</b>},
		{ title: 'Đánh giá', dataIndex: 'adStatus', key: 'adStatus', width: '130px', render: (_, item) => {
			if (can?.edit && isEditing(item, 'adStatus')) {
				return (
					<Select autoFocus defaultOpen allowClear size="small" style={{width: 120}}
						defaultValue={item.adStatus?.key || undefined}
						options={AD_STATUS_OPTIONS}
						onChange={(value) => { onChangeAdStatus(item, value || ''); setEditingCell(null); }}
						onBlur={() => setEditingCell(null)}/>
				);
			}
			return (
				<span style={can?.edit ? {cursor: 'pointer'} : {}} title={can?.edit ? 'Nhấn để chỉnh' : ''}
					onClick={() => can?.edit && setEditingCell({id: item.id, field: 'adStatus'})}>
					{item.adStatus?.label
						? <StatusLabel small type={item.adStatus.badge}>{item.adStatus.label}</StatusLabel>
						: <span style={{color: '#bbb', fontSize: '12px'}}>Chưa đánh giá ✎</span>}
				</span>
			);
		}},
		{ title: 'Trạng thái', dataIndex: 'status', key: 'status', width: '150px', render: (_, item) => {
			if (can?.status && isEditing(item, 'status')) {
				return (
					<Select autoFocus defaultOpen size="small" style={{width: 140}}
						defaultValue={item.status?.key || item.status}
						options={STATUS_OPTIONS}
						onChange={(value) => { onChangeStatus(item, value); setEditingCell(null); }}
						onBlur={() => setEditingCell(null)}/>
				);
			}
			return (
				<span style={can?.status ? {cursor: 'pointer'} : {}} title={can?.status ? 'Nhấn để chỉnh' : ''}
					onClick={() => can?.status && setEditingCell({id: item.id, field: 'status'})}>
					<StatusLabel small type={item.status?.badge}>{item.status?.label}</StatusLabel>
				</span>
			);
		}},
		{ title: '#', dataIndex: 'action', key: 'action', width: '100px', fixed: 'right', render: (_, item) => (
			<MarketingProjectAction
				key={item.id}
				item={item}
				can={can}
				onClickEdit={handleEditClick}
				onClickAssign={handleAssignClick}
					onClickReactivate={handleReactivateClick}
					onClickDelete={handleDeleteClick}
			/>
		)},
	];

	const dataSource = items.map(row => ({key: row.id, ...row}));

	return (
		<div className="table-wrapper">
			<div className="table-box">
				<Table columns={columns} dataSource={dataSource} scroll={{y: tableHeight, x: 'max-content'}} pagination={false} />
			</div>
			<Pagination current={pagination.page} defaultCurrent={1} defaultPageSize={20} pageSize={pagination.limit} total={pagination.total} onChange={onPaginationChange} showSizeChanger={false} />
		</div>
	)
}

export default MarketingProjectTable;
